import { useEffect, useState, Fragment } from 'react';
import Loader from '@/components/Loader';
import { calculateDuration, safeRender } from '@/utils/helperUtils';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { useGetMahadashaQuery } from '@/store/api/Vimshottari/mahadashaApi';
import { useLazyGetAntardashaQuery } from '@/store/api/Vimshottari/antardashaApi';
import {
  useLazyGetPratyantarDashaQuery,
  PratyantarDasha,
} from '@/store/api/Vimshottari/pratyantarDashaApi';
import { useGetSheshaQuery } from '@/store/api/Shesha/sheshaApi';

interface VimshottariDashaProps {
  userId: string;
}

interface Mahadasha {
  planet: string;
  start: string;
  end: string;
}

interface Antardasha {
  planet: string;
  start: string;
  end: string;
}

function parseAnyDate(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);

  const cleanStr = dateStr.trim();
  const nums = cleanStr.match(/\d+/g);

  if (!nums || nums.length < 3) {
    return new Date(cleanStr);
  }

  const n = nums.map(Number);

  let day = 1,
    month = 1,
    year = 1970;

  const n0 = n[0] ?? 0;
  const n1 = n[1] ?? 0;
  const n2 = n[2] ?? 0;

  if (n0 > 1000) {
    year = n0;
    month = n1;
    day = n2;
  } else if (n2 > 1000) {
    day = n0;
    month = n1;
    year = n2;
  } else {
    return new Date(cleanStr);
  }

  const hours = n[3] || 0;
  const minutes = n[4] || 0;
  const seconds = n[5] || 0;

  let adjustedHours = hours;
  if (/PM/i.test(cleanStr) && hours < 12) {
    adjustedHours += 12;
  } else if (/AM/i.test(cleanStr) && hours === 12) {
    adjustedHours = 0;
  }

  return new Date(year, month - 1, day, adjustedHours, minutes, seconds);
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDisplayDate(dateStr: string) {
  if (!dateStr) return '';
  const d = parseAnyDate(dateStr);
  if (isNaN(d.getTime())) return safeRender(dateStr);
  const day = d.getDate().toString().padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strTime = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  return `${day} ${month} ${year}, ${strTime}`;
}

function isCurrentDasha(start: string, end: string): boolean {
  const now = new Date();
  const s = parseAnyDate(start);
  const e = parseAnyDate(end);

  if (isNaN(s.getTime()) || isNaN(e.getTime())) return false;

  return now >= s && now <= e;
}

const getRowStyles = (isCurrent: boolean, isHeader?: boolean) => {
  if (isCurrent) {
    return '!bg-[#FFD700] !bg-none !text-black font-medium';
  }
  return 'hover:opacity-80 transition-opacity';
};

export default function VimshottariDasha({ userId }: VimshottariDashaProps) {
  const {
    data: mahadasha = [],
    isLoading,
    isError,
  } = useGetMahadashaQuery(userId, {
    skip: !userId,
  });

  const { data: sheshaData } = useGetSheshaQuery(userId, {
    skip: !userId,
  });

  const [getAntardasha] = useLazyGetAntardashaQuery();
  const [getPratyantarDasha] = useLazyGetPratyantarDashaQuery();

  const [antardashaMap, setAntardashaMap] = useState<Record<string, Antardasha[]>>({});
  const [pratyantarMap, setPratyantarMap] = useState<Record<string, PratyantarDasha[]>>({});

  const [antardashaLoading, setAntardashaLoading] = useState(false);
  const [pratyantarLoading, setPratyantarLoading] = useState(false);

  const [expandedMahadasha, setExpandedMahadasha] = useState<string | null>(null);
  const [expandedAntardasha, setExpandedAntardasha] = useState<string | null>(null);

  useEffect(() => {
    if (!mahadasha.length) return;

    // Only set initial active mahadasha once
    if (expandedMahadasha === null && !antardashaLoading) {
      let activeMahadasha = mahadasha[0]?.planet || '';
      for (const md of mahadasha) {
        if (isCurrentDasha(md.start, md.end)) {
          activeMahadasha = md.planet;
          break;
        }
      }
      setExpandedMahadasha(activeMahadasha);
    }
  }, [mahadasha]);

  useEffect(() => {
    if (!expandedMahadasha) return;
    if (antardashaMap[expandedMahadasha] || antardashaLoading) return;

    const fetchAntardasha = async () => {
      setAntardashaLoading(true);
      try {
        const res = await getAntardasha({
          userId,
          mahaDasha: expandedMahadasha,
        }).unwrap();

        const data = Array.isArray(res) ? res : [];
        setAntardashaMap((prev) => ({
          ...prev,
          [expandedMahadasha]: data,
        }));

        // Auto-expand active antardasha if this is the currently active Mahadasha
        let isActiveMd = false;
        for (const md of mahadasha) {
          if (md.planet === expandedMahadasha && isCurrentDasha(md.start, md.end)) {
            isActiveMd = true;
            break;
          }
        }

        if (isActiveMd) {
          for (const ad of data) {
            if (isCurrentDasha(ad.start, ad.end)) {
              setExpandedAntardasha(ad.planet);
              break;
            }
          }
        }
      } catch {
        setAntardashaMap((prev) => ({
          ...prev,
          [expandedMahadasha]: [],
        }));
      } finally {
        setAntardashaLoading(false);
      }
    };

    fetchAntardasha();
  }, [expandedMahadasha, userId, getAntardasha, mahadasha]);

  useEffect(() => {
    if (!expandedMahadasha || !expandedAntardasha) return;

    const key = `${expandedMahadasha}-${expandedAntardasha}`;
    if (pratyantarMap[key] || pratyantarLoading) return;

    const fetchPratyantar = async () => {
      setPratyantarLoading(true);
      try {
        const res = await getPratyantarDasha({
          userId,
          mahaDasha: expandedMahadasha,
          antraDasha: expandedAntardasha,
        }).unwrap();

        setPratyantarMap((prev) => ({
          ...prev,
          [key]: Array.isArray(res) ? res : [],
        }));
      } catch {
        setPratyantarMap((prev) => ({
          ...prev,
          [key]: [],
        }));
      } finally {
        setPratyantarLoading(false);
      }
    };

    fetchPratyantar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedAntardasha, expandedMahadasha, userId, getPratyantarDasha]);

  if (isLoading) {
    return (
      <div className="min-h-[300px] flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return <p className="text-center text-red-500">Failed to load Mahadasha</p>;
  }

  return (
    <div className="space-y-10 w-full max-w-full overflow-x-hidden">
      <div className="border border-[#2DAAFD] rounded-xl p-6 overflow-x-auto">
        <h2 className="text-3xl font-bold font-spectral mb-2 text-center text-[#2A185C] uppercase">
          VIMSHOTTARI DASHA
        </h2>

        {sheshaData?.message && (
          <p className="text-center font-lato text-gray-700 text-[15px] mb-6 font-semibold">
            Mahadasha (Venus) Shesha at Birth – {sheshaData.message}
          </p>
        )}

        {antardashaLoading && (
          <div className="flex justify-center items-center my-4">
            <Loader />
          </div>
        )}

        <div className="overflow-x-auto w-full rounded-md shadow-sm border border-[#06186C]">
          <table className="w-full min-w-[800px] border-collapse border-hidden font-18 whitespace-nowrap">
            <thead className="bg-[#031B5B] text-white font-serif uppercase tracking-wide font-semibold">
              <tr className="text-center align-middle">
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight text-left pl-6">
                  Planet
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Duration
                </th>
                <th className="border border-[#031B5B] border-r-white/30 px-4 py-4 leading-tight">
                  Start
                </th>
                <th className="border border-[#031B5B] px-4 py-4 leading-tight">End</th>
              </tr>
            </thead>

            <tbody className="text-gray-900 font-normal font-sans">
              {mahadasha.map((md: Mahadasha) => {
                const isMdExpanded = expandedMahadasha === md.planet;
                const isMdCurrent = isCurrentDasha(md.start, md.end);

                return (
                  <Fragment key={`${md.planet}-${md.start}`}>
                    <tr
                      className={`cursor-pointer select-none border-b border-[#031B5B]/30 even:bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)] odd:bg-white ${getRowStyles(isMdCurrent)}`}
                      onClick={() => setExpandedMahadasha(isMdExpanded ? null : md.planet)}
                    >
                      <td className="px-4 py-[10px] border-r border-[#031B5B]/30 flex items-center gap-2">
                        {isMdExpanded ? (
                          <ChevronDown size={14} className="min-w-[14px]" />
                        ) : (
                          <ChevronRight size={14} className="min-w-[14px]" />
                        )}
                        <span className="font-medium">{md.planet} - Mahadasha</span>
                      </td>
                      <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                        {calculateDuration(md.start, md.end)}
                      </td>
                      <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                        {formatDisplayDate(md.start)}
                      </td>
                      <td className="px-4 py-[10px] text-left">{formatDisplayDate(md.end)}</td>
                    </tr>

                    {isMdExpanded &&
                      antardashaMap[md.planet]?.map((ad: Antardasha) => {
                        const isAdExpanded =
                          expandedAntardasha === ad.planet && expandedMahadasha === md.planet;
                        const isAdCurrent = isCurrentDasha(ad.start, ad.end);

                        return (
                          <Fragment key={`${md.planet}-${ad.planet}-${ad.start}`}>
                            <tr
                              className={`cursor-pointer select-none border-b border-[#031B5B]/30 even:bg-[#61C2FF] odd:bg-white ${getRowStyles(isAdCurrent)}`}
                              onClick={() => setExpandedAntardasha(isAdExpanded ? null : ad.planet)}
                            >
                              <td className="px-4 py-[10px] pl-10 border-r border-[#031B5B]/30 flex items-center gap-2">
                                {isAdExpanded ? (
                                  <ChevronDown size={14} className="min-w-[14px]" />
                                ) : (
                                  <ChevronRight size={14} className="min-w-[14px]" />
                                )}
                                {md.planet} - {ad.planet} Antardasha
                              </td>
                              <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                                {calculateDuration(ad.start, ad.end)}
                              </td>
                              <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                                {formatDisplayDate(ad.start)}
                              </td>
                              <td className="px-4 py-[10px] text-left">
                                {formatDisplayDate(ad.end)}
                              </td>
                            </tr>

                            {isAdExpanded && pratyantarLoading && (
                              <tr className="even:bg-[#61C2FF] odd:bg-white">
                                <td colSpan={4} className="text-center p-4">
                                  <Loader />
                                </td>
                              </tr>
                            )}

                            {isAdExpanded &&
                              !pratyantarLoading &&
                              pratyantarMap[`${md.planet}-${ad.planet}`]?.map(
                                (pd: PratyantarDasha) => {
                                  const isPdCurrent = isCurrentDasha(pd.start, pd.end);
                                  return (
                                    <tr
                                      key={`${md.planet}-${ad.planet}-${pd.planet}`}
                                      className={`border-b border-[#031B5B]/30 even:bg-[#61C2FF] odd:bg-white ${getRowStyles(isPdCurrent)}`}
                                    >
                                      <td className="px-4 py-[10px] pl-[68px] border-r border-[#031B5B]/30">
                                        {md.planet} - {ad.planet} - {pd.planet} Pratyantar
                                      </td>
                                      <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                                        {calculateDuration(pd.start, pd.end)}
                                      </td>
                                      <td className="px-4 py-[10px] border-r border-[#031B5B]/30 text-left">
                                        {formatDisplayDate(pd.start)}
                                      </td>
                                      <td className="px-4 py-[10px] text-left">
                                        {formatDisplayDate(pd.end)}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
